import { Form, Field, defineRule as baseDefineRule } from 'vee-validate/dist/vee-validate.mjs';
import { h, defineComponent, reactive, ref } from 'vue';
import * as allRules from '@vee-validate/rules';

// eslint-disable-next-line import/export -- the wrapped defineRule below intentionally shadows this star re-export (ESM gives local named exports precedence).
export * from 'vee-validate/dist/vee-validate.mjs';

// Map of rule name → message string, populated by extend() calls below.
// Keep parity with legacy vee-validate v2 defaults used in the app.
const customMessages = {
  required: "can't be blank",
  numeric: 'enter a positive number',
  between: 'enter a number between {min} and {max}',
};

// Registry of all rules (built-in + custom) for ValidationProvider validation.
const customRules = {};
// Registry of param names per rule, used for message template substitution.
const customParamNames = {
  between: ['min', 'max'],
};
let validationProviderFallbackCounter = 0;

const FALLBACK_FIELD = 'field';

const isPromise = (value) => !!value && typeof value.then === 'function';
const toArrayParams = (value) => (Array.isArray(value) ? value : []);
const toFieldName = (ctx) => ctx?.field || ctx?.name || FALLBACK_FIELD;
const toParamPlaceholders = (params, paramNames) => Object.fromEntries(
  (paramNames || []).map((name, index) => [name, params[index]]),
);

const interpolateMessage = (template, { params = [], field, paramNames = [] } = {}) => template
  .replace(/\{_field_\}/g, field)
  .replace(/\{(\w+)\}/g, (_, key) => {
    const idx = paramNames.indexOf(key);

    return idx !== -1 && params[idx] !== undefined ? params[idx] : `{${key}}`;
  });

const resolveValidationResult = (name, result, args, ctx, paramNames) => {
  if (result === true) {
    return true;
  }

  if (typeof result === 'string') {
    return result;
  }

  if (typeof result === 'object' && result) {
    if (result.valid === true) {
      return true;
    }

    if (result.valid === false && typeof result.data?.message === 'string') {
      return result.data.message;
    }
  }

  const template = customMessages[name];
  const params = toArrayParams(args);
  const names = paramNames || customParamNames[name] || [];

  if (template) {
    if (typeof template === 'string') {
      return interpolateMessage(template, {
        params,
        field: toFieldName(ctx),
        paramNames: names,
      });
    }

    if (typeof template === 'function') {
      const message = template(toFieldName(ctx), {
        _field_: toFieldName(ctx),
        ...toParamPlaceholders(params, names),
      });

      if (typeof message === 'string') {
        return message;
      }
    }
  }

  return `${toFieldName(ctx)} is not valid.`;
};

const withCompatMessage = (name, validator, paramNames) => (value, args, ctx) => {
  const result = validator(value, args, ctx);

  if (isPromise(result)) {
    return result.then((resolved) => resolveValidationResult(name, resolved, args, ctx, paramNames));
  }

  return resolveValidationResult(name, result, args, ctx, paramNames);
};

// eslint-disable-next-line import/export -- intentionally shadows the star re-export above.
export const defineRule = (name, validator, paramNames = customParamNames[name] || null) => {
  const wrapped = withCompatMessage(name, validator, paramNames);

  baseDefineRule(name, wrapped);
  customRules[name] = wrapped;
};

// Register all @vee-validate/rules globally so legacy `rules="required|..."` strings work.
Object.entries(allRules).forEach(([name, rule]) => {
  if (typeof rule === 'function') {
    defineRule(name, rule);
  }
});

// required_if was removed from @vee-validate/rules v4; register a compat version.
// Syntax: required_if:otherFieldName — required when the other field has a truthy value.
defineRule('required_if', (value, [targetValue]) => {
  if (!targetValue) {return true;}

  return value !== null && value !== undefined && value !== '';
});

// Legacy names used in Vue 2 code paths.
export const ValidationObserver = defineComponent({
  name: 'ValidationObserver',
  inheritAttrs: false,
  setup(_, { attrs, slots, expose }) {
    const formRef = ref(null);
    const flags = reactive({
      invalid: false,
      valid: true,
      dirty: false,
      touched: false,
      failed: false,
    });

    const getFormApi = () => formRef.value;
    const getErrors = () => getFormApi()?.errors?.value || getFormApi()?.getErrors?.() || {};
    const getMeta = () => getFormApi()?.meta?.value || getFormApi()?.getMeta?.() || {};
    const syncFlags = ({ errors = getErrors(), meta = getMeta(), valid } = {}) => {
      const hasErrors = !!errors && Object.keys(errors).length > 0;
      const isValid = typeof valid === 'boolean' ? valid : !hasErrors && meta.valid !== false;

      flags.valid = !!isValid;
      flags.invalid = !isValid;
      flags.failed = !isValid;
      flags.dirty = !!meta.dirty;
      flags.touched = !!meta.touched;
    };
    const validate = async (...args) => {
      const result = await getFormApi()?.validate?.(...args);

      // vee-validate v2 returned a boolean; v4 returns `{ valid, errors, results }`.
      if (typeof result === 'boolean') {
        syncFlags({ valid: result });

        return result;
      }

      if (result?.valid === false && (!result.errors || Object.keys(result.errors).length === 0)) {
        syncFlags({ errors: result.errors, valid: true });

        return true;
      }

      const isValid = !!result?.valid;

      syncFlags({ errors: result?.errors, valid: isValid });

      return isValid;
    };

    expose({
      validate,
      flags,
      getErrors,
      reset: (...args) => getFormApi()?.resetForm?.(...args),
      setErrors: (...args) => getFormApi()?.setErrors?.(...args),
      passes: async (cb, ...args) => {
        const valid = await validate(...args);

        if (valid && typeof cb === 'function') {
          return cb();
        }

        return valid;
      },
    });

    return () => {
      const { slim, tag, as, mode, vid, ...restAttrs } = attrs;
      // Default to a non-<form> tag (matches vee-validate v3's <ValidationObserver>,
      // which rendered a <span>). Rendering a real <form> made any <button> inside
      // submit it natively on click → full-page refresh (e.g. the Apply button in
      // the edit-time popover). Callers validate via .validate()/.passes(), not
      // native submit; an explicit `as`/`tag` still wins.
      const formTag = (as || tag || (slim ? 'div' : 'span'));

      return h(
        Form,
        { ...restAttrs, as: formTag, ref: formRef },
        {
          default: (ctx = {}) => {
            const errors = ctx.errors || {};
            const invalid = Object.keys(errors).length > 0;
            const valid = !invalid;

            return slots.default?.({
              ...ctx,
              invalid,
              valid,
              validate: ctx.validate || validate,
              reset: ctx.resetForm,
              passes: async (cb) => {
                const result = await (ctx.validate ? ctx.validate() : validate());
                const isValid = typeof result === 'boolean' ? result : !!result?.valid;

                if (isValid && typeof cb === 'function') {
                  return cb();
                }

                return isValid;
              },
            });
          },
        },
      );
    };
  },
});

// Wraps Field v4 in a div so class/id attrs from the parent (e.g. ValidationProvider.my-class)
// are applied to a real DOM element. Field v4 is renderless and doesn't forward attrs to the slot.
// We also bridge the native 'input'/'change' events from the div to Field's handleChange so validation
// fires automatically — matching the Vue 2 ValidationProvider behavior.
export const ValidationProvider = defineComponent({
  name: 'ValidationProvider',
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default: undefined,
    },
  },
  setup(props, { attrs, slots, expose }) {
    // Each prop is accepted in camelCase and kebab-case (templates follow the repo-wide
    // `vue/attribute-hyphenation` rule; Vue normalizes either form onto Field's declared props).
    const FIELD_PROPS = new Set(
      ['name', 'rules', 'modelValue', 'validateOnMount', 'validateOnChange',
        'validateOnInput', 'validateOnBlur', 'validateOnModelUpdate', 'bails', 'label', 'standalone',
      ].flatMap((name) => [name, name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)]),
    );

    const fieldRef = ref(null);
    const rootRef = ref(null);
    // Keep an instance-stable fallback field name. If this changes per render,
    // vee-validate keeps re-registering fields and may trigger recursive updates.
    const fallbackFieldName = `field_${++validationProviderFallbackCounter}`;

    const getInputValue = (eventOrValue) => {
      if (
        eventOrValue &&
        typeof eventOrValue === 'object' &&
        'target' in eventOrValue &&
        eventOrValue.target
      ) {
        const target = eventOrValue.target;
        const type = target.type;

        if (type === 'checkbox' || type === 'radio') {
          return !!target.checked;
        }

        return target.value;
      }

      return eventOrValue;
    };

    const syncDomValueToField = () => {
      const root = rootRef.value;
      const input = root?.querySelector?.('input,select,textarea');

      if (!input) {
        return;
      }

      const type = input.type;
      const value = (type === 'checkbox' || type === 'radio') ? !!input.checked : input.value;

      fieldRef.value?.handleChange?.(value, false);
    };

    // Expose Field methods that legacy code accesses via $refs.provider.handleChange / .validate
    const exposed = {
      handleChange: (...args) => fieldRef.value?.handleChange?.(getInputValue(args[0]), args[1]),
      validate: (...args) => {
        syncDomValueToField();

        return fieldRef.value?.validate?.(...args);
      },
      setFlags: ({ touched, dirty } = {}) => {
        if (touched !== undefined) {fieldRef.value?.setTouched?.(!!touched);}
        if (dirty) {syncDomValueToField();}
      },
      setErrors: (...args) => fieldRef.value?.setErrors?.(...args),
      reset: (...args) => fieldRef.value?.reset?.(...args),
    };

    // Vue 2 ValidationProvider exposed `errors` directly on the ref. Field v4 exposes it
    // as a ref under different shapes; surface it as a plain array so legacy code keeps working.
    Object.defineProperty(exposed, 'errors', {
      get: () => {
        const e = fieldRef.value?.errors;

        if (Array.isArray(e)) {return e;}
        if (e && 'value' in e) {return e.value || [];}

        return [];
      },
    });

    expose(exposed);

    // attrs is reactive in Vue 3 setup — read it inside the render fn so reactivity is tracked.
    return () => {
      const toField = Object.fromEntries(
        Object.entries(attrs)
          .filter(([k]) => FIELD_PROPS.has(k))
          .map(([k, v]) => [k === 'model-value' ? 'modelValue' : k, v]),
      );
      // Strip custom-messages (and its camelCase form) + the `slim` flag from DOM attrs —
      // the browser doesn't understand them and `slim` is handled below (renderless).
      const toDom = Object.fromEntries(Object.entries(attrs).filter(([k]) => !FIELD_PROPS.has(k) && k !== 'mode' && k !== 'custom-messages' && k !== 'customMessages' && k !== 'slim'));
      // vee-validate v3 `slim` = renderless: render the slot's own root element instead
      // of wrapping it in a <div> (an extra wrapper breaks layouts that expect the slot
      // root as a direct child, e.g. flex `.form-col` columns).
      const isSlim = attrs.slim !== undefined && attrs.slim !== false;

      // If the caller supplied custom-messages / customMessages, wrap the rules so the custom
      // message is returned directly and overrides any compat/default message.
      const perFieldMessages = attrs['custom-messages'] ?? attrs.customMessages;

      if (perFieldMessages && toField.rules) {
        const rulesStr = toField.rules;

        toField.rules = (value) => {
          // Parse the rule string (e.g. "required|min:3") into individual rule names + params.
          const ruleEntries = String(rulesStr).split('|').map((r) => {
            const separatorIndex = r.indexOf(':');
            const name = separatorIndex === -1 ? r : r.slice(0, separatorIndex);
            const rawParams = separatorIndex === -1 ? '' : r.slice(separatorIndex + 1);
            const params = rawParams ? rawParams.split(',') : [];

            return { name, params };
          });

          for (const { name, params } of ruleEntries) {
            const ruleFn = customRules[name];

            if (!ruleFn) {
              continue;
            }

            const passed = ruleFn(value, params.length ? params : undefined);

            if (passed !== true && passed !== undefined && passed !== null) {
              // Rule failed — return the custom message if provided, otherwise the rule's own message.
              return perFieldMessages[name] ?? (typeof passed === 'string' ? passed : `${name} is not valid.`);
            }
          }

          return true;
        };
      }

      if (props.name !== undefined) {
        toField.name = props.name;
      }

      // Field v4 requires a name prop; provide a fallback so legacy ValidationProvider usage
      // (without explicit name) doesn't trigger Vue warnings.
      if (!toField.name) {
        toField.name = toField.label || fallbackFieldName;
      }

      const getFieldValue = (event) => {
        const parsed = getInputValue(event);

        // Some legacy tests/components trigger DOM "change" on wrapper elements that
        // don't carry a native value; in that case, validate using current modelValue.
        return parsed === undefined ? toField.modelValue : parsed;
      };

      return h(Field, { ...toField, ref: fieldRef }, {
        default: ({ handleChange, handleBlur, errors }) => {
          const normalizedErrors = Array.isArray(errors) ? errors : [];
          const invalid = normalizedErrors.length > 0;
          const content = slots.default?.({ errors: normalizedErrors, invalid });

          // `slim` (vee-validate v3) = renderless: emit the slot content as-is, with no
          // wrapper element and no native-event bridge. This keeps layouts that expect the
          // slot's own root as a direct child intact (e.g. flex `.form-col` columns).
          // Fields whose validation depends on the native-event bridge must NOT use `slim`
          // (they get the <div> below) or must drive validation via `:model-value`.
          if (isSlim) {
            return content;
          }

          return h(
            'div',
            {
              ...toDom,
              ref: rootRef,
              onInput: (event) => handleChange(getFieldValue(event)),
              onChange: (event) => handleChange(getFieldValue(event)),
              onBlur: handleBlur,
            },
            content,
          );
        },
      });
    };
  },
});

export const extend = (name, rule) => {
  const validator = typeof rule === 'function' ? rule : rule.validate;
  const paramNames = Array.isArray(rule.params) ? rule.params : null;

  if (rule.message) {
    customMessages[name] = rule.message;
  }

  if (paramNames) {
    customParamNames[name] = paramNames;
  }

  if (typeof validator === 'function') {
    // vee-validate v4 passes params as a plain array; v2 rules expect a named object.
    // When the rule declares `params: ['name1', ...]`, wrap the validator to convert.
    const wrappedValidator = paramNames
      ? (value, args, ctx) => {
          const paramsObj = Array.isArray(args)
            ? Object.fromEntries(paramNames.map((k, i) => [k, args[i]]))
            : args;

          return validator(value, paramsObj, ctx);
        }
      : validator;

    defineRule(name, wrappedValidator, paramNames);
  }
};
